import axios, { AxiosRequestConfig, AxiosInstance } from 'axios';
import { store } from '../store/store';
import { logoutReducer, setMustChangePasswordReducer } from '../store/reducers/authReducer';
import { getMeThunk } from '../store/middlewares/authThunk';
import { setSession } from '../utils/jwt';
import { HOST_API, getHostApi } from '../config';
import { AUTH_ENDPOINTS } from '../constants/endpoints';
import { AUTH_NOTICE_KEY } from '../constants/index';

const BASE_URL = HOST_API || '';

const apiConfig: AxiosRequestConfig = {
  timeout: 1200000,
  headers: {
    'Content-Type': 'application/json',
    Accept: '*/*',
  },
};

const axiosInstance: AxiosInstance = axios.create({ baseURL: BASE_URL, ...apiConfig });

axiosInstance.interceptors.request.use(
  (config) => {
    config.baseURL = getHostApi();
    let token = store.getState().auth.accessToken;
    if (!token && typeof window !== 'undefined') {
      token = localStorage.getItem('accessToken');
    }
    if (config.headers) {
      config.headers['ngrok-skip-browser-warning'] = 'true';
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// GET /staff/me at most this often after a 403 NOT_ALLOWED
const ROLES_REFRESH_MS = 30000;
let lastRolesRefresh = 0;

// There is no refresh endpoint: on any 401 ("Token expired", "Session ended. Log in again.",
// "This login is deactivated.") the token is dropped and the user goes back to the login
// screen. A 401 from /staff/login itself means wrong credentials and is handled by the form.
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    const { config, response } = error;
    const url: string = config?.url || '';
    const code = response?.data?.detail?.code;

    if (response?.status === 401 && !url.includes(AUTH_ENDPOINTS.LOGIN)) {
      // Shown once on the login page, e.g. "This login is deactivated."
      const detail = response?.data?.detail;
      if (typeof detail === 'string' && store.getState().auth.isAuthenticated) {
        try {
          sessionStorage.setItem(AUTH_NOTICE_KEY, detail);
        } catch (e) {
          // Storage blocked: the login page just shows no reason
        }
      }
      setSession(null);
      store.dispatch(logoutReducer());
      if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/auth/login')) {
        window.location.href = '/auth/login';
      }
    } else if (response?.status === 403 && code === 'PASSWORD_CHANGE_REQUIRED') {
      // The guard sends the user to the "Set your password" screen
      store.dispatch(setMustChangePasswordReducer(true));
    } else if (response?.status === 403 && code === 'NOT_ALLOWED') {
      // The owner may have changed the roles: refresh them so the menu follows
      const now = Date.now();
      if (now - lastRolesRefresh > ROLES_REFRESH_MS) {
        lastRolesRefresh = now;
        store.dispatch(getMeThunk());
      }
    }
    return Promise.reject(error);
  }
);

export default axiosInstance;
